/**
 * Facet helpers — filter and rename CIO facets based on the facet mapping config.
 *
 * Fixes a bug in the hec-composable version: the display name lookup was using
 * the CIO name as a key into the mapping, but the mapping keys are display names
 * and the values are CIO names. The lookup needs to be inverted.
 */
import type { FacetMappingConfig } from "~/hec/config/facet-mapping";
import type { CioFacets } from "~/hec/context/cio-facet-adapter";

/**
 * Filters CIO facets down to the mapped (allowlisted) ones and renames
 * `display_name` to the human-readable name from the mapping.
 *
 * Uses the config's pre-computed inverted mapping (CIO name → display name) for
 * correct lookups. The composable version had a bug where it looked up
 * facetMapping[facet.name] but the keys are display names, not CIO names.
 *
 * `config` comes from the BM `Constructor_FacetMapping` pref resolved in the
 * loader and carried down through `CioFacetProvider`, so the drawer renders
 * exactly the facets the server filtered by.
 */
export const filterAndRenameCioFacets = (
  cioFacets: any[],
  config: FacetMappingConfig,
) =>
  cioFacets
    .filter(facet => config.allowed.has(facet.name))
    .map(facet => ({
      ...facet,
      display_name: config.displayByCioName[facet.name] || facet.display_name,
    }));

/**
 * Mirrors SFRA's `isFacetApplicable` helper at
 * `org_hec/cartridge/client/default/react/helpers/facetUtils.js:29-45`.
 *
 * Returns true when the facet's `lmn-true` option is "applicable" — i.e. the
 * result set contains products requiring a Letter of Medical Necessity.
 *
 * Used by SFRA to gate the "All HSA Eligible" subtitle: SFRA hides the
 * subtitle when this returns true, since the bulk-eligibility claim is
 * misleading when the result set mixes LMN-required and non-LMN products.
 *
 * Logic (verbatim port):
 * - Returns false if the named facet is missing.
 * - Returns false if the facet has no `lmn-true` option.
 * - Returns false when only `lmn-false` is selected (user explicitly
 *   filtered out LMN-required products).
 * - Returns true when both `lmn-true` and `lmn-false` are selected (mixed →
 *   subtitle should hide).
 * - Returns true otherwise (LMN-true is exposed and not explicitly excluded).
 */
export const isFacetApplicable = (
  facets: CioFacets[] | undefined,
  facetName: string,
): boolean => {
  const facet = facets?.find(f => f.name === facetName);
  if (!facet) return false;
  const options = facet.options ?? [];
  const hasLmnTrue = options.some(o => o?.value === "lmn-true");
  if (!hasLmnTrue) return false;
  const lmnTrueSelected = options.some(
    o => o?.value === "lmn-true" && o.status === "selected",
  );
  const lmnFalseSelected = options.some(
    o => o?.value === "lmn-false" && o.status === "selected",
  );
  if (lmnFalseSelected && !lmnTrueSelected) return false;
  return true;
};

export const extractValueFromSelectedFacets = (
  selectedFacetFilters: Record<string, { value: string }[]>,
) => {
  const result: Record<string, string[]> = {};
  Object.entries(selectedFacetFilters).forEach(([category, values]) => {
    result[category] = values.map(({ value }) => value);
  });
  return result;
};
